// src/pages/finance-portal/payroll-dashboard/PayrollDashboard.js

import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Card, CardContent, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Alert, Skeleton, Button,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const RUN_STATUS_COLORS = {
    DRAFT: { bg: '#f1f5f9', color: '#64748b' },
    PENDING: { bg: '#fef3c7', color: '#92400e' },
    APPROVED: { bg: '#dbeafe', color: '#1e40af' },
    PROCESSING: { bg: '#e0e7ff', color: '#3730a3' },
    COMPLETED: { bg: '#dcfce7', color: '#166534' },
    VOID: { bg: '#f3f4f6', color: '#6b7280' },
};

const FILING_STATUS_COLORS = {
    PENDING: { bg: '#fef3c7', color: '#92400e' },
    DUE: { bg: '#fecaca', color: '#991b1b' },
    FILED: { bg: '#dcfce7', color: '#166534' },
    LATE: { bg: '#fecaca', color: '#991b1b' },
};

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

function SummaryCard({ icon, label, value, color, onClick }) {
    return (
        <Card
            onClick={onClick}
            sx={{
                borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.15s',
                '&:hover': onClick ? { borderColor: ACCENT, transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' } : {},
            }}
        >
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
                <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {icon}
                </Box>
                <Box>
                    <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#64748b', fontWeight: 600, mb: 0.25 }}>{label}</Typography>
                    <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 800, color: '#1e293b' }}>{value}</Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

export default function PayrollDashboard() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [employeeCount, setEmployeeCount] = useState(0);
    const [recentRuns, setRecentRuns] = useState([]);
    const [upcomingFilings, setUpcomingFilings] = useState([]);
    const [contractorSummary, setContractorSummary] = useState([]);
    const [lastPayrollAmount, setLastPayrollAmount] = useState(0);
    const [nextPayDate, setNextPayDate] = useState(null);
    const [ytdPayrollCost, setYtdPayrollCost] = useState(0);

    useEffect(() => {
        const fetchDashboard = async () => {
            setLoading(true);
            setError(null);
            try {
                // Fetch all dashboard data in parallel
                const [empRes, runsRes, filingsRes, contractorRes] = await Promise.all([
                    axiosInstance.get('/api/finance/payroll/employees', { params: { per_page: 1 } }).catch(() => null),
                    axiosInstance.get('/api/finance/payroll/runs', { params: { per_page: 5 } }).catch(() => null),
                    axiosInstance.get('/api/finance/payroll/tax-filings', { params: { status: 'DUE', per_page: 5 } }).catch(() => null),
                    axiosInstance.get('/api/finance/payroll/contractor-payments/ytd-summary').catch(() => null),
                ]);

                if (empRes?.data?.success) {
                    setEmployeeCount(empRes.data.total || 0);
                }

                if (runsRes?.data?.success) {
                    const runs = runsRes.data.data || [];
                    setRecentRuns(runs);
                    if (runs.length > 0) {
                        const completed = runs.find(r => r.status === 'COMPLETED');
                        if (completed) {
                            setLastPayrollAmount(completed.total_net_pay || completed.total_gross_pay || 0);
                        }
                        // Next pay date from the most recent non-completed run
                        const upcoming = runs.find(r => r.status !== 'COMPLETED' && r.status !== 'VOID');
                        if (upcoming) {
                            setNextPayDate(upcoming.pay_date);
                        }
                    }
                    // Calculate YTD from all completed runs
                    const ytd = runs.filter(r => r.status === 'COMPLETED')
                        .reduce((sum, r) => sum + (parseFloat(r.total_gross_pay) || 0), 0);
                    setYtdPayrollCost(ytd);
                }

                if (filingsRes?.data?.success) {
                    setUpcomingFilings(filingsRes.data.data || []);
                }

                if (contractorRes?.data?.success) {
                    setContractorSummary(contractorRes.data.data || []);
                }
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to load dashboard');
            } finally {
                setLoading(false);
            }
        };
        fetchDashboard();
    }, []);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <DashboardIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Payroll Dashboard</Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Overview of payroll operations</Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} md={3}>
                        {loading ? <Skeleton variant="rounded" height={90} sx={{ borderRadius: '16px' }} /> : (
                            <SummaryCard
                                icon={<PeopleIcon sx={{ fontSize: 24, color: '#3b82f6' }} />}
                                label="Total Employees"
                                value={employeeCount}
                                color="#3b82f6"
                                onClick={() => navigate('/finance-portal/payroll-employees')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={6} md={3}>
                        {loading ? <Skeleton variant="rounded" height={90} sx={{ borderRadius: '16px' }} /> : (
                            <SummaryCard
                                icon={<CalendarTodayIcon sx={{ fontSize: 24, color: '#8b5cf6' }} />}
                                label="Next Pay Date"
                                value={nextPayDate ? formatDate(nextPayDate) : '—'}
                                color="#8b5cf6"
                            />
                        )}
                    </Grid>
                    <Grid item xs={6} md={3}>
                        {loading ? <Skeleton variant="rounded" height={90} sx={{ borderRadius: '16px' }} /> : (
                            <SummaryCard
                                icon={<AttachMoneyIcon sx={{ fontSize: 24, color: ACCENT }} />}
                                label="Last Payroll"
                                value={formatCurrency(lastPayrollAmount)}
                                color={ACCENT}
                                onClick={() => navigate('/finance-portal/payroll-runs')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={6} md={3}>
                        {loading ? <Skeleton variant="rounded" height={90} sx={{ borderRadius: '16px' }} /> : (
                            <SummaryCard
                                icon={<TrendingUpIcon sx={{ fontSize: 24, color: '#f59e0b' }} />}
                                label="YTD Payroll Cost"
                                value={formatCurrency(ytdPayrollCost)}
                                color="#f59e0b"
                            />
                        )}
                    </Grid>
                </Grid>

                {/* Recent Payroll Runs */}
                <Grid container spacing={3}>
                    <Grid item xs={12} lg={7}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>Recent Payroll Runs</Typography>
                                    <Button size="small" onClick={() => navigate('/finance-portal/payroll-runs')}
                                        sx={{ fontFamily, textTransform: 'none', fontSize: '0.8rem', color: ACCENT }}>View All</Button>
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                {['Run #', 'Pay Date', 'Employees', 'Gross Pay', 'Net Pay', 'Status'].map(h => (
                                                    <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {loading ? (
                                                [1, 2, 3].map(i => (
                                                    <TableRow key={i}>{[1, 2, 3, 4, 5, 6].map(j => <TableCell key={j}><Skeleton height={32} /></TableCell>)}</TableRow>
                                                ))
                                            ) : recentRuns.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No payroll runs yet</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : recentRuns.map(run => (
                                                <TableRow key={run.id} sx={{ '&:hover': { bgcolor: '#f8fafc' }, cursor: 'pointer' }}
                                                    onClick={() => navigate(`/finance-portal/payroll-runs/${run.id}`)}>
                                                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: ACCENT }}>{run.run_number || `#${run.id}`}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{formatDate(run.pay_date)}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{run.employee_count || 0}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{formatCurrency(run.total_gross_pay)}</TableCell>
                                                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{formatCurrency(run.total_net_pay)}</TableCell>
                                                    <TableCell sx={bodyCellSx}>
                                                        <Chip label={getStatusLabel(run.status)} size="small"
                                                            sx={{
                                                                fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                                bgcolor: (RUN_STATUS_COLORS[run.status] || RUN_STATUS_COLORS.DRAFT).bg,
                                                                color: (RUN_STATUS_COLORS[run.status] || RUN_STATUS_COLORS.DRAFT).color,
                                                            }} />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>

                        {/* Contractor YTD Summary */}
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>Contractor YTD Summary</Typography>
                                    <Button size="small" onClick={() => navigate('/finance-portal/contractor-payments')}
                                        sx={{ fontFamily, textTransform: 'none', fontSize: '0.8rem', color: ACCENT }}>View All</Button>
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                {['Contractor', 'YTD Paid', 'Payments', '1099 Required'].map(h => (
                                                    <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {loading ? (
                                                [1, 2].map(i => (
                                                    <TableRow key={i}>{[1, 2, 3, 4].map(j => <TableCell key={j}><Skeleton height={32} /></TableCell>)}</TableRow>
                                                ))
                                            ) : contractorSummary.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No contractor payments</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : contractorSummary.map((c, idx) => (
                                                <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                                    <TableCell sx={bodyCellSx}>{c.contractor_name || '—'}</TableCell>
                                                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{formatCurrency(c.total_paid)}</TableCell>
                                                    <TableCell sx={bodyCellSx}>{c.payment_count || 0}</TableCell>
                                                    <TableCell sx={bodyCellSx}>
                                                        <Chip
                                                            label={(c.total_paid || 0) >= 600 ? 'Yes' : 'No'}
                                                            size="small"
                                                            sx={{
                                                                fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                                bgcolor: (c.total_paid || 0) >= 600 ? '#fef3c7' : '#f1f5f9',
                                                                color: (c.total_paid || 0) >= 600 ? '#92400e' : '#64748b',
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Upcoming Tax Filings */}
                    <Grid item xs={12} lg={5}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>Upcoming Tax Filings</Typography>
                                    <Button size="small" onClick={() => navigate('/finance-portal/tax-filings')}
                                        sx={{ fontFamily, textTransform: 'none', fontSize: '0.8rem', color: ACCENT }}>View All</Button>
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                {['Filing', 'Due Date', 'Status'].map(h => (
                                                    <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {loading ? (
                                                [1, 2, 3].map(i => (
                                                    <TableRow key={i}>{[1, 2, 3].map(j => <TableCell key={j}><Skeleton height={32} /></TableCell>)}</TableRow>
                                                ))
                                            ) : upcomingFilings.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No upcoming filings</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : upcomingFilings.map(f => (
                                                <TableRow key={f.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                                                        {f.filing_type} {f.quarter ? `Q${f.quarter}` : ''} {f.tax_year}
                                                    </TableCell>
                                                    <TableCell sx={{ ...bodyCellSx, color: f.status === 'DUE' || f.status === 'LATE' ? '#dc2626' : '#1e293b', fontWeight: 600 }}>
                                                        {formatDate(f.due_date)}
                                                    </TableCell>
                                                    <TableCell sx={bodyCellSx}>
                                                        <Chip label={getStatusLabel(f.status)} size="small"
                                                            sx={{
                                                                fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                                bgcolor: (FILING_STATUS_COLORS[f.status] || FILING_STATUS_COLORS.PENDING).bg,
                                                                color: (FILING_STATUS_COLORS[f.status] || FILING_STATUS_COLORS.PENDING).color,
                                                            }} />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}
